/**
 * Yarnly Crochet — product.js
 *
 * Product page runtime: variant selection through the Section Rendering API
 * (no variant JSON is ever shipped), the media gallery and its lightbox,
 * deferred video / 3D media, pickup availability, the sticky add to cart bar
 * (inside main-product, or the standalone landing section that follows the
 * hero) and the share control. Lazy product recommendations
 * (<yarnly-product-recommendations>) live in theme.js so they work on
 * every page.
 *
 * Plain ES2020, no modules. Everything is read from DOM data attributes and
 * every registration is guarded so the file is safe to evaluate twice.
 */
(function () {
  'use strict';

  const S = window.Yarnly;
  if (!S) return;

  /* Nodes the variant picker swaps after a re-render, keyed by id prefix. */
  const SWAP_TARGETS = ['price', 'variant-picker', 'buy-buttons', 'sku', 'inventory', 'pickup', 'sticky-atc'];

  function galleryForSection(sectionId) {
    const galleries = document.querySelectorAll('yarnly-media-gallery');
    for (let i = 0; i < galleries.length; i += 1) {
      if (String(galleries[i].getAttribute('data-section-id')) === String(sectionId)) return galleries[i];
    }
    return null;
  }

  function closestFrom(node, selector) {
    return node && typeof node.closest === 'function' ? node.closest(selector) : null;
  }

  /* ------------------------------------------------------------------------ */
  /* 3D media                                                                  */
  /* ------------------------------------------------------------------------ */

  // model-viewer-ui and shopify-xr are only worth their bytes when the page
  // actually has a 3D model, so they are requested lazily and only once.
  function upgradeModelViewers() {
    if (!window.Shopify || typeof window.Shopify.ModelViewerUI !== 'function') return;
    document.querySelectorAll('[data-media-type="model"] model-viewer:not([data-model-ready])').forEach((element) => {
      element.setAttribute('data-model-ready', 'true');
      try {
        const ui = new window.Shopify.ModelViewerUI(element);
        element.yarnlyModelViewerUI = ui;
      } catch (error) {
        element.removeAttribute('data-model-ready');
      }
    });
  }

  function setupModels() {
    if (!document.querySelector('[data-media-type="model"]') || !window.Shopify?.loadFeatures) return;
    if (S._yarnlyModelFeatures) {
      upgradeModelViewers();
      return;
    }
    S._yarnlyModelFeatures = true;
    window.Shopify.loadFeatures(
      [
        { name: 'model-viewer-ui', version: '1.0' },
        { name: 'shopify-xr', version: '1.0' }
      ],
      function (errors) {
        if (errors) {
          S._yarnlyModelFeatures = false;
          return;
        }
        if (window.ShopifyXR && typeof window.ShopifyXR.setupXRElements === 'function') {
          window.ShopifyXR.setupXRElements();
        }
        upgradeModelViewers();
      }
    );
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-variant-picker>                                                 */
  /* ------------------------------------------------------------------------ */

  // Collects the checked option_value ids in option order and asks the section
  // for a fresh render, then swaps the parts of the page that changed.
  if (!customElements.get('yarnly-variant-picker')) {
    class YarnlyVariantPicker extends HTMLElement {
      constructor() {
        super();
        this.onChange = this.onChange.bind(this);
        this.requestId = 0;
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      get sectionId() {
        return this.getAttribute('data-section-id') || '';
      }

      // Built in Liquid: product.url plus ?view=<template.suffix> on alternate
      // templates, so the section re-render comes from the same JSON template
      // (a section id only exists in the template that declares it).
      get productUrl() {
        return this.getAttribute('data-product-url') || '';
      }

      get valueNodes() {
        return Array.from(this.querySelectorAll('[data-option-value-id]'));
      }

      selectedIds() {
        return this.valueNodes
          .filter((node) => (node.tagName === 'OPTION' ? node.selected : node.checked))
          .map((node) => node.getAttribute('data-option-value-id'))
          .filter(Boolean);
      }

      onChange(event) {
        const target = event.target;
        let source = target;
        if (target.tagName === 'SELECT') source = target.options[target.selectedIndex] || null;
        if (!source || !source.hasAttribute('data-option-value-id')) return;

        const ids = this.selectedIds();
        if (!ids.length) return;

        // Combined listings point some option values at a different product.
        const override = source.getAttribute('data-product-url');
        const base = override && override !== this.productUrl ? override : this.productUrl;
        if (!base || typeof window.fetch !== 'function') return;

        this.load(base, ids, target.id || '');
      }

      load(baseUrl, ids, focusId) {
        const sectionId = this.sectionId;
        const token = (this.requestId += 1);
        const separator = baseUrl.indexOf('?') === -1 ? '?' : '&';
        const url =
          baseUrl +
          separator +
          'section_id=' +
          encodeURIComponent(sectionId) +
          '&option_values=' +
          ids.join(',');

        this.setBusy(true);

        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error('Variant request failed');
            return response.text();
          })
          .then((text) => {
            if (token !== this.requestId) return;
            const doc = S.utils.parseHTML(text);
            if (!doc.querySelector('[data-product-root]')) throw new Error('Missing product root');
            this.render(doc, sectionId, focusId);
            this.setBusy(false);
          })
          .catch(() => {
            // A real link to the product is always in the markup; go there.
            window.location.href = baseUrl;
          });
      }

      render(doc, sectionId, focusId) {
        SWAP_TARGETS.forEach((name) => {
          const id = name + '-' + sectionId;
          const current = document.getElementById(id);
          const next = doc.getElementById(id);
          if (!current || !next) return;
          current.innerHTML = next.innerHTML;
        });

        const buyButtons = doc.getElementById('buy-buttons-' + sectionId);
        const idInput = buyButtons
          ? buyButtons.querySelector('input[name="id"]')
          : doc.querySelector('input[name="id"]');
        const submit = buyButtons ? buyButtons.querySelector('[type="submit"]') : null;
        const variantId = idInput && !idInput.disabled && idInput.value ? idInput.value : null;
        const available = !!(submit && !submit.disabled);

        const selectedMedia = doc.querySelector('[data-gallery-item][data-selected]');
        const mediaId = selectedMedia ? selectedMedia.getAttribute('data-media-id') : null;
        const gallery = galleryForSection(sectionId);
        if (gallery && mediaId && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(mediaId);
        }

        // The rendered picker names its product (a combined listing may have
        // switched to a sibling product); fall back to our own attribute.
        const nextPicker = doc.getElementById('variant-picker-' + sectionId);
        if (nextPicker) {
          ['data-product-id', 'data-product-url'].forEach((name) => {
            const value = nextPicker.getAttribute(name);
            if (value) this.setAttribute(name, value);
          });
        }
        const productId = this.getAttribute('data-product-id') || null;

        if (this.getAttribute('data-update-url') === 'true') {
          S.utils.updateURLParam('variant', variantId);
        }

        // Payload contract: { sectionId, variantId, available, mediaId, productId }.
        // Subscribers filter on sectionId (same section) or productId (landing
        // CTAs anywhere on the page).
        S.pubsub.publish('variant:change', {
          sectionId: sectionId,
          variantId: variantId,
          available: available,
          mediaId: mediaId,
          productId: productId
        });

        if (focusId) {
          const focusTarget = document.getElementById(focusId);
          if (focusTarget && typeof focusTarget.focus === 'function') {
            focusTarget.focus({ preventScroll: true });
          }
        }

        setupModels();
      }

      setBusy(on) {
        if (on) {
          this.setAttribute('aria-busy', 'true');
          this.loadingButton = document.getElementById('ProductSubmitButton-' + this.sectionId);
          if (this.loadingButton) {
            this.loadingButton.disabled = true;
            this.loadingButton.classList.add('btn--loading');
          }
          return;
        }
        this.removeAttribute('aria-busy');
        const button = this.loadingButton;
        this.loadingButton = null;
        // A swapped-in button already carries the right state from the server;
        // only the button we disabled ourselves is restored.
        if (button && button.isConnected) {
          button.disabled = false;
          button.classList.remove('btn--loading');
        }
      }
    }
    customElements.define('yarnly-variant-picker', YarnlyVariantPicker);
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-media-gallery>                                                  */
  /* ------------------------------------------------------------------------ */

  // Scroll-snap viewer plus a thumbnail rail. setActiveMedia(id) is public so
  // the variant picker can bring the selected variant's media into view.
  if (!customElements.get('yarnly-media-gallery')) {
    class YarnlyMediaGallery extends HTMLElement {
      constructor() {
        super();
        this.onThumbClick = this.onThumbClick.bind(this);
        this.onThumbKeyDown = this.onThumbKeyDown.bind(this);
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
      }

      connectedCallback() {
        this.viewer = this.querySelector('[data-gallery-viewer]');
        this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
        this.thumbList = this.querySelector('[data-gallery-thumbnails]');
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'));
        this.counter = this.querySelector('[data-gallery-counter]');
        this.prevButton = this.querySelector('[data-gallery-prev]');
        this.nextButton = this.querySelector('[data-gallery-next]');

        if (this.thumbList) {
          this.thumbList.addEventListener('click', this.onThumbClick);
          this.thumbList.addEventListener('keydown', this.onThumbKeyDown);
        }
        if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
        if (this.viewer) {
          this.onScroll = S.utils.debounce(() => this.syncFromScroll(), 120);
          this.viewer.addEventListener('scroll', this.onScroll, { passive: true });
        }

        this.unsubscribe = S.pubsub.subscribe('variant:change', this.onVariantChange);
        this.updateCounter();
      }

      disconnectedCallback() {
        if (this.thumbList) {
          this.thumbList.removeEventListener('click', this.onThumbClick);
          this.thumbList.removeEventListener('keydown', this.onThumbKeyDown);
        }
        if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
        if (this.viewer && this.onScroll) this.viewer.removeEventListener('scroll', this.onScroll);
        if (this.unsubscribe) this.unsubscribe();
      }

      onVariantChange(payload) {
        if (!payload || String(payload.sectionId) !== String(this.getAttribute('data-section-id'))) return;
        if (payload.mediaId) this.setActiveMedia(payload.mediaId);
      }

      get activeIndex() {
        const index = this.items.findIndex((item) => item.hasAttribute('data-selected'));
        return index === -1 ? 0 : index;
      }

      setActiveMedia(mediaId) {
        if (!mediaId) return;
        const target = this.items.find((item) => item.getAttribute('data-media-id') === String(mediaId));
        if (!target) return;

        if (!target.hasAttribute('data-selected')) {
          this.items.forEach((item) => {
            if (item === target) {
              item.setAttribute('data-selected', '');
              return;
            }
            item.removeAttribute('data-selected');
            const deferred = item.querySelector('yarnly-deferred-media');
            if (deferred && typeof deferred.pause === 'function') deferred.pause();
          });
          this.syncThumbs(mediaId);
          const index = this.items.indexOf(target) + 1;
          if (S.strings.imageAvailable) {
            S.utils.announce(S.utils.formatTemplate(S.strings.imageAvailable, { index: index }));
          }
        }

        this.scrollToItem(target);
        this.updateCounter();
      }

      syncThumbs(mediaId) {
        this.thumbs.forEach((thumb) => {
          if (thumb.getAttribute('data-media-id') === String(mediaId)) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
      }

      scrollToItem(item) {
        const viewer = this.viewer;
        if (!viewer || !item) return;
        const behavior = S.utils.isReducedMotion() ? 'auto' : 'smooth';
        if (viewer.scrollWidth - viewer.clientWidth > 2) {
          const left =
            item.getBoundingClientRect().left - viewer.getBoundingClientRect().left + viewer.scrollLeft;
          if (typeof viewer.scrollTo === 'function') viewer.scrollTo({ left: left, behavior: behavior });
          else viewer.scrollLeft = left;
        } else if (viewer.scrollHeight - viewer.clientHeight > 2) {
          const top = item.getBoundingClientRect().top - viewer.getBoundingClientRect().top + viewer.scrollTop;
          if (typeof viewer.scrollTo === 'function') viewer.scrollTo({ top: top, behavior: behavior });
          else viewer.scrollTop = top;
        }
      }

      // Keeps the counter and thumbnails honest when the viewer is swiped.
      syncFromScroll() {
        const viewer = this.viewer;
        if (!viewer || this.items.length < 2) return;
        if (viewer.scrollWidth - viewer.clientWidth <= 2) return;
        const edge = viewer.getBoundingClientRect().left;
        let best = null;
        let bestDistance = Infinity;
        this.items.forEach((item) => {
          const distance = Math.abs(item.getBoundingClientRect().left - edge);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = item;
          }
        });
        if (!best || best.hasAttribute('data-selected')) return;
        this.items.forEach((item) => {
          if (item === best) item.setAttribute('data-selected', '');
          else item.removeAttribute('data-selected');
        });
        this.syncThumbs(best.getAttribute('data-media-id'));
        this.updateCounter();
      }

      updateCounter() {
        const total = this.items.length;
        const current = this.activeIndex + 1;
        if (this.counter) {
          const template = this.getAttribute('data-counter-template');
          this.counter.textContent = template
            ? S.utils.formatTemplate(template, { current: current, total: total })
            : current + ' / ' + total;
        }
        if (this.prevButton) this.prevButton.setAttribute('aria-disabled', String(current <= 1));
        if (this.nextButton) this.nextButton.setAttribute('aria-disabled', String(current >= total));
      }

      step(delta) {
        const next = this.activeIndex + delta;
        if (next < 0 || next >= this.items.length) return;
        const item = this.items[next];
        if (item) this.setActiveMedia(item.getAttribute('data-media-id'));
      }

      onPrev(event) {
        event.preventDefault();
        this.step(-1);
      }

      onNext(event) {
        event.preventDefault();
        this.step(1);
      }

      onThumbClick(event) {
        const thumb = closestFrom(event.target, '[data-gallery-thumbnail]');
        if (!thumb) return;
        event.preventDefault();
        this.setActiveMedia(thumb.getAttribute('data-media-id'));
      }

      onThumbKeyDown(event) {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (keys.indexOf(event.key) === -1) return;
        const active = closestFrom(document.activeElement, '[data-gallery-thumbnail]');
        const current = this.thumbs.indexOf(active);
        if (current === -1) return;
        event.preventDefault();

        let next = current;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = current - 1;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current + 1;
        else if (event.key === 'Home') next = 0;
        else next = this.thumbs.length - 1;

        next = Math.max(0, Math.min(this.thumbs.length - 1, next));
        const thumb = this.thumbs[next];
        if (!thumb) return;
        thumb.focus();
        this.setActiveMedia(thumb.getAttribute('data-media-id'));
      }
    }
    customElements.define('yarnly-media-gallery', YarnlyMediaGallery);
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-deferred-media>                                                 */
  /* ------------------------------------------------------------------------ */

  // Poster image plus a play button; the real <video>, iframe or model-viewer
  // lives in a <template> and is only inserted when the visitor asks for it.
  if (!customElements.get('yarnly-deferred-media')) {
    class YarnlyDeferredMedia extends HTMLElement {
      constructor() {
        super();
        this.onActivate = this.onActivate.bind(this);
        this.loaded = false;
      }

      connectedCallback() {
        this.button = this.querySelector('[data-deferred-play]');
        if (this.button) this.button.addEventListener('click', this.onActivate);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onActivate);
      }

      onActivate(event) {
        if (event) event.preventDefault();
        this.load();
      }

      load() {
        if (this.loaded) {
          this.play();
          return;
        }
        const template = this.querySelector('template');
        if (!template) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'product-media__player';
        wrapper.appendChild(template.content.cloneNode(true));
        this.appendChild(wrapper);
        this.loaded = true;
        this.setAttribute('data-loaded', 'true');
        if (this.getAttribute('data-media-type') === 'model') upgradeModelViewers();
        this.play();
      }

      play() {
        const media = this.querySelector('video, iframe, model-viewer');
        if (!media) return;
        if (typeof media.focus === 'function') media.focus({ preventScroll: true });
        if (this.getAttribute('data-autoplay') === 'true' && typeof media.play === 'function') {
          const result = media.play();
          if (result && typeof result.catch === 'function') result.catch(() => {});
        }
      }

      pause() {
        this.querySelectorAll('video').forEach((video) => {
          if (typeof video.pause === 'function') video.pause();
        });
      }
    }
    customElements.define('yarnly-deferred-media', YarnlyDeferredMedia);
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-media-lightbox>                                                 */
  /* ------------------------------------------------------------------------ */

  if (!customElements.get('yarnly-media-lightbox')) {
    class YarnlyMediaLightbox extends HTMLElement {
      constructor() {
        super();
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onTriggerClick = this.onTriggerClick.bind(this);
      }

      connectedCallback() {
        this.track = this.querySelector('[data-lightbox-track]');
        this.slides = Array.from(this.querySelectorAll('[data-lightbox-slide]'));
        this.prevButton = this.querySelector('[data-lightbox-prev]');
        this.nextButton = this.querySelector('[data-lightbox-next]');
        if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
        this.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onTriggerClick);
      }

      disconnectedCallback() {
        if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
        this.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('click', this.onTriggerClick);
      }

      // The zoom buttons open the modal through theme.js; this only decides
      // which image the modal lands on.
      onTriggerClick(event) {
        const trigger = closestFrom(event.target, '[data-modal-open]');
        if (!trigger) return;
        if (trigger.getAttribute('data-modal-open') !== this.getAttribute('data-modal-id')) return;
        const mediaId = trigger.getAttribute('data-media-id');
        if (!mediaId) return;
        window.requestAnimationFrame(() => this.goToMedia(mediaId, true));
      }

      get currentIndex() {
        if (!this.track || !this.slides.length) return 0;
        const edge = this.track.getBoundingClientRect().left;
        let best = 0;
        let bestDistance = Infinity;
        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - edge);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = index;
          }
        });
        return best;
      }

      goToMedia(mediaId, instant) {
        const index = this.slides.findIndex((slide) => slide.getAttribute('data-media-id') === String(mediaId));
        if (index === -1) return;
        this.goToIndex(index, instant);
      }

      goToIndex(index, instant) {
        const slide = this.slides[index];
        if (!slide || !this.track) return;
        const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
        const behavior = instant || S.utils.isReducedMotion() ? 'auto' : 'smooth';
        if (typeof this.track.scrollTo === 'function') this.track.scrollTo({ left: left, behavior: behavior });
        else this.track.scrollLeft = left;
      }

      step(delta) {
        const next = Math.max(0, Math.min(this.slides.length - 1, this.currentIndex + delta));
        this.goToIndex(next);
      }

      onPrev(event) {
        event.preventDefault();
        this.step(-1);
      }

      onNext(event) {
        event.preventDefault();
        this.step(1);
      }

      onKeyDown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      }
    }
    customElements.define('yarnly-media-lightbox', YarnlyMediaLightbox);
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-pickup-availability>                                            */
  /* ------------------------------------------------------------------------ */

  // Renders sections/pickup-availability.liquid for the selected variant.
  if (!customElements.get('yarnly-pickup-availability')) {
    class YarnlyPickupAvailability extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.requestId = 0;
      }

      connectedCallback() {
        this.variantId = this.getAttribute('data-variant-id') || '';
        this.addEventListener('click', this.onClick);
        this.unsubscribe = S.pubsub.subscribe('variant:change', this.onVariantChange);
        this.fetchAvailability();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.unsubscribe) this.unsubscribe();
      }

      onVariantChange(payload) {
        if (!payload || String(payload.sectionId) !== String(this.getAttribute('data-section-id'))) return;
        this.variantId = payload.variantId ? String(payload.variantId) : '';
        this.setAttribute('data-variant-id', this.variantId);
        this.fetchAvailability();
      }

      onClick(event) {
        const button = closestFrom(event.target, '[data-pickup-refresh]');
        if (!button) return;
        event.preventDefault();
        this.fetchAvailability();
      }

      fetchAvailability() {
        if (typeof window.fetch !== 'function') return;
        if (!this.variantId) {
          this.innerHTML = '';
          return;
        }
        const token = (this.requestId += 1);
        const root = String(S.routes.root || '/').replace(/\/+$/, '');
        const url = root + '/variants/' + encodeURIComponent(this.variantId) + '/?section_id=pickup-availability';

        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error('Pickup request failed');
            return response.text();
          })
          .then((text) => {
            if (token !== this.requestId) return;
            const doc = S.utils.parseHTML(text);
            const source = doc.querySelector('[data-pickup]');
            this.innerHTML = source ? source.outerHTML : '';
          })
          .catch(() => {
            if (token === this.requestId) this.renderError();
          });
      }

      renderError() {
        this.innerHTML = '';
        const wrapper = document.createElement('div');
        wrapper.className = 'pickup pickup--error';

        const text = document.createElement('p');
        text.className = 'pickup__summary small';
        text.textContent = S.strings.pickupUnavailable || '';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn--link pickup__toggle';
        button.setAttribute('data-pickup-refresh', '');
        button.textContent = S.strings.refresh || '';

        wrapper.appendChild(text);
        wrapper.appendChild(button);
        this.appendChild(wrapper);
      }
    }
    customElements.define('yarnly-pickup-availability', YarnlyPickupAvailability);
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-sticky-atc>                                                     */
  /* ------------------------------------------------------------------------ */

  // Compact buy bar fixed to the bottom of the viewport. Two usages:
  //  - the standalone sticky-atc section: a section of its own that follows
  //    the hero buy box, found through the first [data-main-atc] on the page
  //    (button.closest('[data-product-root]'));
  //  - inside main-product: the product root it sits in.
  //
  // Attributes
  //   data-section-id    its own section id
  //   data-appear-after  'hero_button' (default): visible whenever the hero's
  //                      [data-main-atc] is out of view, above or below the
  //                      fold; 'half_viewport': visible while
  //                      scrollY > innerHeight / 2, whatever the hero does.
  //                      With no hero button on the page it behaves as
  //                      half_viewport.
  //   data-desktop       'true' lets it show at 990px and up (the CSS reads
  //                      it); without it the bar is phone/tablet only.
  // Children
  //   input[data-sticky-variant]      the hidden variant id
  //   [type=submit][data-label]       keeps data-label while available,
  //                                   S.strings.soldOut / unavailable otherwise
  //   [data-sticky-label]             where the label text goes (the button
  //                                   itself when absent and text-only)
  //   [data-sticky-price]             optional; filled from the hero's
  //                                   #buy-buttons-<id> template[data-price-source]
  //
  // Hidden while any drawer or modal is open. Showing settles through a 250ms
  // debounce so it never flashes during layout; hiding for an overlay is
  // immediate. While a bar is visible at the current width, body gets
  // .has-sticky-bar and <html> gets --sticky-bar-height so the page end can be
  // padded clear of it. It re-observes the hero after every variant swap and
  // every editor section load, and selecting its own section in the Theme
  // Editor forces it visible.
  const STICKY_OVERLAY_EVENTS = ['drawer:open', 'drawer:close', 'modal:open', 'modal:close'];

  function overlayIsOpen() {
    return !!document.querySelector('yarnly-drawer.is-open, yarnly-modal.is-open');
  }

  function isDesktop() {
    if (S.utils.matchesMedia) return S.utils.matchesMedia('(min-width: 990px)');
    return typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 990px)').matches;
  }

  // Read from the DOM rather than counted, so several bars (or a bar that is
  // removed by the editor) can never leave the padding stuck.
  function syncStickyBarPadding() {
    let padded = false;
    let height = 0;
    document.querySelectorAll('yarnly-sticky-atc.is-visible').forEach((bar) => {
      if (typeof bar.shownAtThisWidth === 'function' && !bar.shownAtThisWidth()) return;
      padded = true;
      height = Math.max(height, bar.offsetHeight || 0);
    });
    if (document.body) document.body.classList.toggle('has-sticky-bar', padded);
    const style = document.documentElement.style;
    if (!padded) style.setProperty('--sticky-bar-height', '0px');
    else if (height > 0) style.setProperty('--sticky-bar-height', Math.ceil(height) + 'px');
    else style.removeProperty('--sticky-bar-height'); // not laid out yet: the CSS fallback applies
  }

  if (!customElements.get('yarnly-sticky-atc')) {
    class YarnlyStickyAtc extends HTMLElement {
      constructor() {
        super();
        [
          'onIntersect',
          'onVariantChange',
          'onOverlay',
          'onScroll',
          'onResize',
          'onSectionLoad',
          'onSectionSelect',
          'onSectionDeselect',
          'applyVisibility',
          'evaluateScroll'
        ].forEach((name) => {
          this[name] = this[name].bind(this);
        });
        this.buttonOutOfView = false;
        this.scrolledPast = false;
        this.overlayOpen = false;
        this.forced = false;
        this.ticking = false;
        this.hero = null;
      }

      connectedCallback() {
        this.sectionId = this.getAttribute('data-section-id') || '';
        this.applyVisibilityDebounced = S.utils.debounce(this.applyVisibility, 250);
        this.onResizeDebounced = S.utils.debounce(this.onResize, 150);
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0 });
        }
        this.unsubscribers = [S.pubsub.subscribe('variant:change', this.onVariantChange)].concat(
          STICKY_OVERLAY_EVENTS.map((name) => S.pubsub.subscribe(name, this.onOverlay))
        );
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResizeDebounced);
        document.addEventListener('shopify:section:load', this.onSectionLoad);
        document.addEventListener('shopify:section:unload', this.onSectionLoad);
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);

        this.overlayOpen = overlayIsOpen();
        this.observeTarget();
        this.evaluateScroll();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        (this.unsubscribers || []).forEach((unsubscribe) => unsubscribe());
        this.unsubscribers = [];
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResizeDebounced);
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
        document.removeEventListener('shopify:section:unload', this.onSectionLoad);
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
        this.classList.remove('is-visible');
        syncStickyBarPadding();
      }

      // The buy box this bar stands in for: the product root it lives in
      // (main-product), else the first [data-main-atc] on the page (the hero).
      resolveHero() {
        const ownRoot = this.closest('[data-product-root]');
        let button = ownRoot ? ownRoot.querySelector('[data-main-atc]') : null;
        if (!button) button = document.querySelector('[data-main-atc]');
        const root = button ? button.closest('[data-product-root]') : ownRoot;
        let sectionId = root ? root.getAttribute('data-section-id') || '' : '';
        if (!sectionId && button) {
          const wrapper = button.closest('[id^="buy-buttons-"]');
          if (wrapper) sectionId = wrapper.id.slice('buy-buttons-'.length);
        }
        this.hero = { button: button, root: root, sectionId: sectionId || this.sectionId };
        return this.hero;
      }

      get appearAfter() {
        if (this.getAttribute('data-appear-after') === 'half_viewport') return 'half_viewport';
        return this.hero && this.hero.button ? 'hero_button' : 'half_viewport';
      }

      shownAtThisWidth() {
        return this.getAttribute('data-desktop') === 'true' || !isDesktop();
      }

      observeTarget() {
        const hero = this.resolveHero();
        if (!this.observer) return;
        this.observer.disconnect();
        if (this.appearAfter !== 'hero_button') return;
        this.observer.observe(hero.button);
      }

      onIntersect(entries) {
        entries.forEach((entry) => {
          if (this.hero && this.hero.button && entry.target !== this.hero.button) return;
          this.buttonOutOfView = !entry.isIntersecting;
        });
        this.applyVisibilityDebounced();
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        const next =
          typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame : (fn) => setTimeout(fn, 16);
        next(() => {
          this.ticking = false;
          this.evaluateScroll();
        });
      }

      evaluateScroll() {
        const viewport = window.innerHeight || document.documentElement.clientHeight || 0;
        const y = window.scrollY || window.pageYOffset || 0;
        const past = y > viewport / 2;
        if (past === this.scrolledPast) return;
        this.scrolledPast = past;
        if (this.appearAfter === 'half_viewport') this.applyVisibilityDebounced();
      }

      onResize() {
        this.evaluateScroll();
        syncStickyBarPadding();
      }

      onOverlay() {
        this.overlayOpen = overlayIsOpen();
        if (this.overlayOpen) this.applyVisibility();
        else this.applyVisibilityDebounced();
      }

      // The editor replaces whole sections: the observed hero button may now be
      // a detached node, so find the live one again.
      onSectionLoad() {
        if (!this.isConnected) return;
        this.observeTarget();
        this.evaluateScroll();
        this.applyVisibilityDebounced();
      }

      onSectionSelect(event) {
        if (!event.detail || String(event.detail.sectionId) !== String(this.sectionId)) return;
        this.forced = true;
        this.applyVisibility();
      }

      onSectionDeselect(event) {
        if (!event.detail || String(event.detail.sectionId) !== String(this.sectionId)) return;
        this.forced = false;
        this.applyVisibility();
      }

      get shouldShow() {
        if (this.forced) return true;
        if (this.overlayOpen) return false;
        return this.appearAfter === 'half_viewport' ? this.scrolledPast : this.buttonOutOfView;
      }

      applyVisibility() {
        this.classList.toggle('is-visible', this.shouldShow);
        syncStickyBarPadding();
      }

      onVariantChange(payload) {
        const hero = this.hero || this.resolveHero();
        const heroSectionId = hero.sectionId || this.sectionId;
        if (!payload || String(payload.sectionId) !== String(heroSectionId)) return;
        const variantId = payload.variantId ? String(payload.variantId) : '';
        const available = !!variantId && payload.available === true;

        const input = this.querySelector('[data-sticky-variant]');
        if (input) {
          input.value = variantId;
          input.disabled = !variantId;
        }
        const submit = this.querySelector('[type="submit"]');
        if (submit) submit.disabled = !available;
        const labelNode = this.querySelector('[data-sticky-label]');
        const labelSource = submit && submit.hasAttribute('data-label') ? submit : labelNode;
        S.utils.setButtonLabel(submit, labelNode, S.utils.variantButtonLabel(labelSource, variantId, available));

        const price = this.querySelector('[data-sticky-price]');
        const source =
          S.utils.priceSourceFor(heroSectionId) ||
          (hero.root && hero.root.isConnected ? hero.root.querySelector('template[data-price-source]') : null);
        if (price && source) price.innerHTML = source.innerHTML;

        // #buy-buttons-<id> (and, inside main-product, this bar) were just
        // swapped, so the observed button is a detached node now.
        this.observeTarget();
      }
    }
    customElements.define('yarnly-sticky-atc', YarnlyStickyAtc);
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-read-more>                                                      */
  /* ------------------------------------------------------------------------ */

  // Collapses a long block of copy on phones only, where a wall of text pushes
  // everything that persuades below the thumb. Desktop always shows the whole
  // thing: hidden content is missed content (NN/g), so this is a mobile
  // affordance, never a place to file information away.
  //
  //   <yarnly-read-more class="read-more" data-max-height="480">
  //     …content…
  //     <button type="button" class="read-more__toggle" data-read-more-toggle
  //       data-label-more="Read more" data-label-less="Read less"
  //       aria-expanded="false" hidden>Read more</button>
  //   </yarnly-read-more>
  //
  // The element sets --read-more-max and the data-collapsed attribute; the CSS
  // owns the clamp itself. Without JS nothing is ever hidden.
  if (!customElements.get('yarnly-read-more')) {
    class YarnlyReadMore extends HTMLElement {
      constructor() {
        super();
        this.onToggle = this.onToggle.bind(this);
        this.update = this.update.bind(this);
        this.expanded = true;
        this.userExpanded = false;
      }

      connectedCallback() {
        this.toggle = this.querySelector('[data-read-more-toggle]');
        this.content = this.querySelector('[data-read-more-content], .read-more__content');
        if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
        this.onResize = S.utils.debounce(this.update, 150);
        window.addEventListener('resize', this.onResize);
        this.update();
        // Fonts and images settle a frame later; measure again once they have.
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(this.update);
        }
      }

      disconnectedCallback() {
        if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
      }

      get maxHeight() {
        const value = parseInt(this.getAttribute('data-max-height'), 10);
        return value > 0 ? value : 0;
      }

      // The CSS breakpoint, asked the same way the CSS asks it. innerWidth is
      // the fallback for environments without matchMedia.
      get isNarrow() {
        if (typeof window.matchMedia === 'function') {
          return window.matchMedia('(max-width: 749px)').matches;
        }
        return window.innerWidth < 750;
      }

      update() {
        const toggle = this.toggle;
        if (!toggle) return;
        const max = this.maxHeight;

        if (!max || !this.isNarrow) {
          this.release();
          return;
        }

        // scrollHeight reports the full content height whether or not the
        // clamp is currently applied, so this stays stable across re-measures
        // — wherever the CSS chooses to hang the max-height.
        this.style.setProperty('--read-more-max', max + 'px');
        const measured = this.content || this;
        if (measured.scrollHeight <= max) {
          this.release();
          return;
        }

        toggle.hidden = false;
        if (!this.userExpanded) this.setExpanded(false);
      }

      // Back to plain, uncollapsed content with no toggle in the way.
      release() {
        this.style.removeProperty('--read-more-max');
        if (this.toggle) this.toggle.hidden = true;
        this.setExpanded(true);
      }

      setExpanded(expanded) {
        this.expanded = expanded;
        if (expanded) this.removeAttribute('data-collapsed');
        else this.setAttribute('data-collapsed', '');

        const toggle = this.toggle;
        if (!toggle) return;
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        const label = expanded
          ? toggle.getAttribute('data-label-less')
          : toggle.getAttribute('data-label-more');
        if (label) toggle.textContent = label;
      }

      onToggle(event) {
        event.preventDefault();
        const nextExpanded = this.hasAttribute('data-collapsed');
        this.userExpanded = nextExpanded;
        this.setExpanded(nextExpanded);
        // Collapsing shortens the page above the reader; without this the
        // toggle jumps out from under the finger that just pressed it.
        if (!nextExpanded) this.scrollTopIntoView();
      }

      scrollTopIntoView() {
        if (typeof this.getBoundingClientRect !== 'function') return;
        if (this.getBoundingClientRect().top >= 0) return;
        if (typeof this.scrollIntoView !== 'function') return;
        this.scrollIntoView({
          block: 'start',
          behavior: S.utils.isReducedMotion() ? 'auto' : 'smooth'
        });
      }
    }
    customElements.define('yarnly-read-more', YarnlyReadMore);
  }

  // The editor swaps a section's HTML before the browser has laid it out, so
  // the measurement taken in connectedCallback can be worthless. Take it again.
  function refreshReadMore(root) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('yarnly-read-more').forEach((element) => {
      if (typeof element.update === 'function') element.update();
    });
    if (scope !== document && scope.tagName === 'YARNLY-READ-MORE' && typeof scope.update === 'function') {
      scope.update();
    }
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-share>                                                          */
  /* ------------------------------------------------------------------------ */

  // Native share sheet where it exists, clipboard copy everywhere else. The
  // markup works on its own: the disclosure holds the plain URL.
  if (!customElements.get('yarnly-share')) {
    class YarnlyShare extends HTMLElement {
      constructor() {
        super();
        this.onShare = this.onShare.bind(this);
        this.onCopy = this.onCopy.bind(this);
      }

      connectedCallback() {
        this.details = this.querySelector('[data-share-details]');
        this.summary = this.querySelector('[data-share-button]');
        this.copyButton = this.querySelector('[data-share-copy]');
        this.input = this.querySelector('[data-share-input]');
        this.status = this.querySelector('[data-share-status]');

        if (this.summary && navigator.share) this.summary.addEventListener('click', this.onShare);
        if (this.copyButton) this.copyButton.addEventListener('click', this.onCopy);
      }

      disconnectedCallback() {
        if (this.summary) this.summary.removeEventListener('click', this.onShare);
        if (this.copyButton) this.copyButton.removeEventListener('click', this.onCopy);
      }

      get shareUrl() {
        return this.getAttribute('data-share-url') || window.location.href;
      }

      onShare(event) {
        event.preventDefault();
        navigator
          .share({ title: this.getAttribute('data-share-title') || document.title, url: this.shareUrl })
          .catch(() => {});
      }

      onCopy(event) {
        event.preventDefault();
        const done = () => this.announceCopied();
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(this.shareUrl).then(done, () => this.selectInput());
          return;
        }
        this.selectInput();
      }

      selectInput() {
        if (!this.input) return;
        this.input.focus();
        this.input.select();
        try {
          if (document.execCommand('copy')) this.announceCopied();
        } catch (error) {
          // Leave the URL selected so it can be copied by hand.
        }
      }

      announceCopied() {
        const message = S.strings.copied || '';
        if (this.status) this.status.textContent = message;
        S.utils.announce(message);
      }
    }
    customElements.define('yarnly-share', YarnlyShare);
  }

  /* ------------------------------------------------------------------------ */
  /* Boot                                                                      */
  /* ------------------------------------------------------------------------ */

  if (!S._yarnlyProductBound) {
    S._yarnlyProductBound = true;
    const start = () => {
      setupModels();
      refreshReadMore(document);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
    document.addEventListener('shopify:section:load', (event) => {
      setupModels();
      refreshReadMore(event.target);
    });
  }
})();
